'use client'
import { createContext, useContext, useEffect, useState } from 'react'
import type { Credentials } from '../../entities'
import { getCredentials, removeCredentials, signIn } from '../../session'
import { type AuthTokenPayload } from '../../session'
import { getConfig } from '@/tools/config'
import { fetcher, removeAuthorization } from '@/tools/fetcher'
import { useErrorHandling } from '@/tools/o11y/client'
import { useWhatsUp } from '@/tools/whats-up'

type AuthState = {
  isLoggedIn: boolean
  credentials: Credentials | null
  logOut: () => Promise<void>
  logIn: (
    loginPayload: AuthTokenPayload,
    config?: { headers: Record<string, string> }
  ) => Promise<void>
  isLoading: boolean
}

// Create the context object
const AuthContext = createContext<AuthState>({
  isLoading: false,
  isLoggedIn: false,
  credentials: null,
  logIn: async () => {
    // noop
  },
  logOut: async () => {
    // noop
  },
})

// Create the provider component
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { execWithErrorTracking } = useErrorHandling()
  const [credentials, setCredentials] = useState<Credentials | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { getWhatsUp } = useWhatsUp()

  useEffect(() => {
    execWithErrorTracking(() => {
      const _credentials = getCredentials()
      setCredentials(_credentials)
      setIsLoading(false)
    })
  }, [execWithErrorTracking])

  useEffect(() => {
    if (!credentials) {
      return
    }
    execWithErrorTracking(async () => {
      await getWhatsUp()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentials])

  const value: AuthState = {
    isLoading,
    credentials,
    isLoggedIn: Boolean(credentials),
    logIn: async (loginPayload, config) => {
      await signIn(loginPayload, config)
      setCredentials(getCredentials())
    },
    logOut: async () => {
      const cookieCreadentials = getCredentials()
      if (cookieCreadentials) {
        const { apiUrl } = getConfig()
        await fetcher.post(`${apiUrl}/oauth/revoke`, { token: cookieCreadentials?.refreshToken })
        removeCredentials()
      }
      removeAuthorization()
      setCredentials(null)
    },
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used inside a <AuthProvider>')
  }
  return context
}
